"use client"

import { usePage } from "@inertiajs/react"
import {
  BarChartSquare02,
  Beaker02,
  Colors,
  Compass03,
  Home02,
  LinkExternal01,
  MessagePlusCircle,
  MusicNote01,
  Route,
  Send01,
  Users01,
} from "@untitledui/icons"
import type { HTMLAttributes } from "react"

import {
  adminCollabsPath,
  adminCoverPressPath,
  adminOnwardsDestinationsPath,
  adminOnwardsLabPath,
  adminOnwardsReachPath,
  adminPlaylistsPath,
  adminUsersPath,
  adminWarmStartsPath,
  dashboardPath,
  ownedPlaylistsPath,
  playlistsPath,
} from "@/routes"
import type { SharedData } from "@/types"

import { BrandLockup } from "./brand-lockup"
import { MobileNavigationHeader } from "./mobile-header"
import { NavItem } from "./nav-item"
import { UserMenu } from "./user-menu"

interface SidebarNavItem {
  label: string
  href: string
  icon: React.FC<HTMLAttributes<HTMLOrSVGElement>>
  badge?: React.ReactNode
}

/* The workspace: dashboard home, the member's own playlists, and the public
 * browse. Discover and My Playlists open the editorial playlist world; the rail
 * is the account/admin desk those exits set out from. */
const primaryNav: SidebarNavItem[] = [
  { label: "Dashboard", href: dashboardPath(), icon: Home02 },
  { label: "My Playlists", href: ownedPlaylistsPath(), icon: MusicNote01 },
  { label: "Discover", href: playlistsPath(), icon: Compass03 },
]

/* Admin-only, appended for `visitor.admin` under a labelled divider. The
 * Onwards Lab route only exists in development, so its link is compiled out of
 * the production bundle (`import.meta.env.DEV`) — no dead link ships. */
const adminNav: SidebarNavItem[] = [
  { label: "Warm Invites", href: adminWarmStartsPath(), icon: Send01 },
  { label: "Users", href: adminUsersPath(), icon: Users01 },
  { label: "Collabs", href: adminCollabsPath(), icon: MessagePlusCircle },
  {
    label: "Playlists",
    href: adminPlaylistsPath(),
    icon: Route,
  },
  {
    label: "Onward destinations",
    href: adminOnwardsDestinationsPath(),
    icon: LinkExternal01,
  },
  {
    label: "Reach & referrals",
    href: adminOnwardsReachPath(),
    icon: BarChartSquare02,
  },
  { label: "Cover Press", href: adminCoverPressPath(), icon: Colors },
  ...(import.meta.env.DEV
    ? [{ label: "Onwards Lab", href: adminOnwardsLabPath(), icon: Beaker02 }]
    : []),
]

const MAIN_SIDEBAR_WIDTH = 260
// Breathing room around the floating rail, plus a wider gap before the content
// column so the page doesn't sit jammed against the rail.
const MAIN_SIDEBAR_GUTTER = 12
const MAIN_CONTENT_GAP = 24

/**
 * The Sidebar — the authenticated shell's rail.
 *
 * A Manila (`surface-grouped`) rail one tonal step deeper than the Oat content
 * canvas, so it reads as chrome without a hard border. Active nav carries its
 * weight in ink on a Paper White proof; the admin group is gated on
 * `visitor.admin`. Mounted on the dashboard and admin pages (and settings, via
 * `SettingsLayout`).
 */
export const Sidebar = () => {
  const { url, props: pageProps } = usePage<SharedData>()
  const isAdmin = Boolean(pageProps.visitor?.admin)
  const path = url.split("?")[0]

  const items = isAdmin ? [...primaryNav, ...adminNav] : primaryNav

  // Longest-prefix-wins active matching: on `/playlists/owned` both Discover
  // (`/playlists`) and My Playlists (`/playlists/owned`) prefix-match, but only
  // the longer (more specific) href is current. Also covers `/admin/warm_starts/new`.
  const isCurrent = (href: string) => {
    const matches = items.filter(
      (item) => path === item.href || path.startsWith(item.href + "/"),
    )
    if (matches.length === 0) return false
    const longest = matches.reduce((a, b) =>
      b.href.length > a.href.length ? b : a,
    )
    return longest.href === href
  }

  const renderItem = (item: SidebarNavItem) => (
    <li key={item.label}>
      <NavItem
        icon={item.icon}
        href={item.href}
        badge={item.badge}
        type="link"
        current={isCurrent(item.href)}
      >
        {item.label}
      </NavItem>
    </li>
  )

  const content = (
    <aside
      style={{ "--width": `${MAIN_SIDEBAR_WIDTH}px` } as React.CSSProperties}
      className="border-secondary bg-surface-grouped flex h-full w-full max-w-full flex-col overflow-auto overscroll-contain pt-5 shadow-xs md:border-r lg:w-(--width) lg:rounded-xl lg:border"
    >
      <div className="px-5">
        <BrandLockup />
      </div>

      <nav aria-label="Primary" className="mt-7 flex flex-1 flex-col px-3">
        <ul className="flex flex-col gap-0.5">{primaryNav.map(renderItem)}</ul>

        {isAdmin && (
          <div className="border-secondary mt-5 border-t pt-5">
            <p className="text-tertiary px-3 pb-1.5 text-xs font-semibold tracking-[0.12em] uppercase">
              Admin
            </p>
            <ul className="flex flex-col gap-0.5">
              {adminNav.map(renderItem)}
            </ul>
          </div>
        )}
      </nav>

      <div className="border-secondary mt-auto border-t px-3 py-3">
        <UserMenu />
      </div>
    </aside>
  )

  return (
    <>
      <MobileNavigationHeader>{content}</MobileNavigationHeader>

      <div
        style={{ padding: MAIN_SIDEBAR_GUTTER }}
        className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex"
      >
        {content}
      </div>

      <div
        style={{
          paddingLeft:
            MAIN_SIDEBAR_WIDTH + MAIN_SIDEBAR_GUTTER + MAIN_CONTENT_GAP,
        }}
        className="invisible hidden lg:sticky lg:top-0 lg:bottom-0 lg:left-0 lg:block"
      />
    </>
  )
}
